import { useTheme } from "expo-router/react-navigation";
import { Sparkles } from "lucide-react-native";
import { FC, useMemo } from "react";
import { View } from "react-native";

import { GroupedBarChart, useCopyableChart } from "@/components/charts";
import { Box, Card, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export interface AwarenessReportData {
  brands: { id: string; name: string }[];
  /** Aided counts per brand (multi-select tally on the prompted question). */
  aidedCounts: number[];
  /** Unaided counts per brand (open-ended/top-of-mind tally). */
  unaidedCounts: number[];
  sampleSize: number;
}

interface AwarenessReportProps {
  data: AwarenessReportData;
  showSampleEditorialSummary?: boolean;
}

/* ── Component ──────────────────────────────────────────── */

const AwarenessReport: FC<AwarenessReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const primary = theme.colors.primary;

  const { chartRef, copyButton } = useCopyableChart();

  const fmtPctRound = (n: number) =>
    i18n.numberToPercentage(n, { precision: 0 });

  /* "Top of mind" is the brand most often cited unaided. */
  const topOfMindIdx = useMemo(() => {
    let best = 0;
    data.unaidedCounts.forEach((v, idx) => {
      if (v > (data.unaidedCounts[best] ?? 0)) best = idx;
    });
    return best;
  }, [data.unaidedCounts]);

  const topOfMindUnaidedPct =
    data.sampleSize > 0
      ? ((data.unaidedCounts[topOfMindIdx] ?? 0) / data.sampleSize) * 100
      : 0;

  /* No answers yet: render the shell, hand the chart empty groups (it shows
     its own "No data available"), and show "—" instead of a fabricated leader —
     mirroring how `PricingResult` shows "—" rather than a fabricated price. */
  const hasData = data.sampleSize > 0;

  const groups = useMemo(
    () => [
      {
        key: "unaided",
        label: i18n.t("useCasePage.awareness.sampleReport.chart.unaidedLabel"),
        points: data.brands.map((b, idx) => ({
          x: b.name,
          y: data.unaidedCounts[idx] ?? 0,
        })),
        respondentCount: data.sampleSize,
      },
      {
        key: "aided",
        label: i18n.t("useCasePage.awareness.sampleReport.chart.aidedLabel"),
        points: data.brands.map((b, idx) => ({
          x: b.name,
          y: data.aidedCounts[idx] ?? 0,
        })),
        respondentCount: data.sampleSize,
      },
    ],

    [data, i18n]
  );

  if (data.brands.length === 0) {
    return null;
  }

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.awareness.sampleReport.summary.body")}
          headline={i18n.t(
            "useCasePage.awareness.sampleReport.summary.headline"
          )}
        />
      )}

      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-3 lg:flex-row lg:items-center lg:gap-5">
          <VStack className="gap-2 lg:flex-1">
            <HStack className="items-center gap-4">
              <Box
                className="w-14 h-14 rounded-2xl items-center justify-center shrink-0"
                style={{ backgroundColor: primary }}
              >
                <Sparkles color="white" size={26} strokeWidth={2.2} />
              </Box>
              <VStack className="flex-1 gap-1">
                <Text className="text-2xs font-semibold text-primary-600 uppercase tracking-wide">
                  {i18n.t("useCasePage.awareness.sampleReport.topOfMind.badge")}
                </Text>
                <Text className="text-xl font-bold text-typography-900 tracking-tight">
                  {hasData ? data.brands[topOfMindIdx].name : "—"}
                </Text>
              </VStack>
            </HStack>
            <Text className="text-xs text-typography-500 lg:pl-[72px]">
              {i18n.t("useCasePage.awareness.sampleReport.topOfMind.subtitle")}
            </Text>
          </VStack>
          <VStack className="items-center lg:items-end gap-0">
            <Text
              className="text-3xl font-bold tracking-tight"
              style={{ color: primary }}
            >
              {hasData ? fmtPctRound(topOfMindUnaidedPct) : "—"}
            </Text>
            <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
              {i18n.t(
                "useCasePage.awareness.sampleReport.topOfMind.unaidedLabel"
              )}
            </Text>
          </VStack>
        </VStack>
      </Card>

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {copyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.awareness.sampleReport.chart.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.sampleSize })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.awareness.sampleReport.chart.subtitle")}
            </Text>
          </VStack>
          <View ref={chartRef} style={{ width: "100%" }}>
            <GroupedBarChart groups={hasData ? groups : []} />
          </View>
        </VStack>
      </Card>
    </VStack>
  );
};

export default AwarenessReport;
